/**
 * Media Gallery Custom Element
 * <media-gallery> - Product image carousel with thumbnails
 */

class MediaGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.totalSlides = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
  }

  connectedCallback() {
    this.carousel = this.querySelector('[data-carousel]');
    this.slides = this.querySelectorAll('[data-slide]');
    this.thumbnails = this.querySelectorAll('[data-thumbnail]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');

    this.currentIndex = parseInt(this.dataset.initialIndex) || 0;
    this.totalSlides = this.slides.length;

    if (this.totalSlides === 0) return;

    this.init();
    this.bindEvents();
  }

  disconnectedCallback() {
    // Cleanup event listeners
    document.removeEventListener('variant:changed', this.handleVariantChange);
  }

  init() {
    // Show initial slide
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === this.currentIndex);
    });
    this.updateThumbnails();

    // Expose to global for external access
    const sectionId = this.dataset.sectionId;
    if (sectionId) {
      window.the4MediaGallery = window.the4MediaGallery || {};
      window.the4MediaGallery[sectionId] = this;
    }
  }

  bindEvents() {
    // Thumbnail clicks
    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(index);
      });
    });

    // Navigation arrows
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.prev();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.next();
      });
    }

    // Keyboard navigation
    this.addEventListener('keydown', this.handleKeydown.bind(this));

    // Touch swipe
    if (this.carousel) {
      this.carousel.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.carousel.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
    }

    // Variant change listener
    this.handleVariantChange = this.onVariantChange.bind(this);
    document.addEventListener('variant:changed', this.handleVariantChange);
  }

  goToSlide(index) {
    if (index < 0) index = this.totalSlides - 1;
    if (index >= this.totalSlides) index = 0;

    this.currentIndex = index;

    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === this.currentIndex);
    });

    this.updateThumbnails();
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('slide:changed', {
      detail: { index: this.currentIndex, mediaId: this.getCurrentMediaId() }
    }));
  }

  next() {
    this.goToSlide(this.currentIndex + 1);
  }

  prev() {
    this.goToSlide(this.currentIndex - 1);
  }

  goToMediaId(mediaId) {
    this.slides.forEach((slide, index) => {
      if (slide.dataset.mediaId == mediaId) {
        this.goToSlide(index);
      }
    });
  }

  getCurrentMediaId() {
    const activeSlide = this.slides[this.currentIndex];
    return activeSlide ? activeSlide.dataset.mediaId : null;
  }

  updateThumbnails() {
    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === this.currentIndex);
    });

    // Scroll active thumbnail into view
    const activeThumb = this.thumbnails[this.currentIndex];
    if (activeThumb) {
      activeThumb.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }

  handleKeydown(e) {
    if (e.key === 'ArrowLeft') {
      this.prev();
    } else if (e.key === 'ArrowRight') {
      this.next();
    }
  }

  handleTouchStart(e) {
    this.touchStartX = e.changedTouches[0].screenX;
  }

  handleTouchEnd(e) {
    this.touchEndX = e.changedTouches[0].screenX;
    this.handleSwipe();
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.next(); // Swipe left
      } else {
        this.prev(); // Swipe right
      }
    }
  }

  onVariantChange(event) {
    const variant = event.detail?.variant;
    if (variant && variant.featured_media) {
      this.goToMediaId(variant.featured_media.id);
    }
  }
}

// Register custom element
if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

